import React, { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import { EllipsisVertical } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ScrollArea from "../ui/shared/ScrollArea";
import { useCampaigns } from "@/hooks/useCampaigns";
import { useAssistants } from "@/hooks/useAssistants";
import { usePipelines } from "@/hooks/usePipelines";
import { toast } from "sonner";

// Define AccountCampaign type directly to avoid Database type conflicts
type AccountCampaign = {
  id: string
  account_id: string
  name: string
  assistant_id: string | null
  trigger_type: string
  trigger_keywords: string[] | null
  tags: string[] | null
  pipeline_id: string | null
  pipeline_stage_id: string | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

interface CampaignEditProps {
  campaign: AccountCampaign;
}

const CampaignEdit: React.FC<CampaignEditProps> = ({ campaign }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updateCampaign, deleteCampaign } = useCampaigns();
  const { assistants } = useAssistants();
  const { pipelines, pipelineStages } = usePipelines();

  const [formData, setFormData] = useState({
    name: campaign.name,
    assistant_id: campaign.assistant_id || '',
    trigger_type: campaign.trigger_type,
    trigger_keywords: campaign.trigger_keywords || [],
    tags: campaign.tags || [],
    pipeline_id: campaign.pipeline_id || '',
    pipeline_stage_id: campaign.pipeline_stage_id || '',
    is_active: campaign.is_active
  });

  const [keywordInput, setKeywordInput] = useState('');

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.trigger_keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        trigger_keywords: [...prev.trigger_keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      trigger_keywords: prev.trigger_keywords.filter((k: string) => k !== keyword)
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome da campanha é obrigatório');
      return;
    }

    if (!formData.trigger_type) {
      toast.error('Tipo de gatilho é obrigatório');
      return;
    }

    if (formData.pipeline_id && formData.pipeline_id !== 'none' && (!formData.pipeline_stage_id || formData.pipeline_stage_id === 'none')) {
      toast.error('Selecione uma etapa para o pipeline selecionado');
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        name: formData.name,
        assistant_id: formData.assistant_id === 'none' ? null : formData.assistant_id || null,
        trigger_type: formData.trigger_type,
        trigger_keywords: formData.trigger_keywords,
        tags: formData.tags,
        pipeline_id: formData.pipeline_id === 'none' ? null : formData.pipeline_id || null,
        pipeline_stage_id: formData.pipeline_stage_id === 'none' ? null : formData.pipeline_stage_id || null,
        is_active: formData.is_active
      };

      const success = await updateCampaign(campaign.id, updateData);

      if (success) {
        toast.success('Campanha atualizada com sucesso!');
        setOpen(false);
      } else {
        toast.error('Erro ao atualizar campanha. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao atualizar campanha:', error);
      toast.error('Erro ao atualizar campanha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Tem certeza que deseja excluir esta campanha?')) {
      return;
    }

    setLoading(true);

    try {
      const success = await deleteCampaign(campaign.id);

      if (success) {
        toast.success('Campanha removida com sucesso!');
        setOpen(false);
      } else {
        toast.error('Erro ao remover campanha. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao deletar campanha:', error);
      toast.error('Erro ao remover campanha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      // Reset form data when opening
      setFormData({
        name: campaign.name,
        assistant_id: campaign.assistant_id || '',
        trigger_type: campaign.trigger_type,
        trigger_keywords: campaign.trigger_keywords || [],
        tags: campaign.tags || [],
        pipeline_id: campaign.pipeline_id || '',
        pipeline_stage_id: campaign.pipeline_stage_id || '',
        is_active: campaign.is_active
      });
    }
  };

  // Filtrar stages baseado no pipeline selecionado
  const availableStages = pipelineStages.filter(stage => 
    stage.pipeline_id === formData.pipeline_id
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button className="w-[34px] h-[34px] flex justify-center items-center border-[1px] border-[#3F3F464D] bg-neutral-600 rounded-[8px]">
          <EllipsisVertical size={20} color="#FAFAFA" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="!bg-[#18181B] p-1 w-[95px] flex flex-col gap-1 border-[#3F3F464D] border">
        <Dialog>
          <DialogTrigger asChild>
            <div className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px]">
              Editar
            </div>
          </DialogTrigger>
          <DialogContent className="flex !max-w-[600px] w-full p-0 border-[#3F3F464D] bg-[#18181B] flex-col">
            <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
              <div className="w-full flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold text-white">Editar Campanha</DialogTitle>
                </DialogHeader>

                <form className="w-full flex flex-col gap-4">
                  {/* Nome */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="name">Nome da Campanha *</Label>
                    <Input
                      placeholder="Nome da campanha"
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                      type="text"
                      required
                    />
                  </div>

                  {/* Assistente */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="assistant">Assistente</Label>
                    <Select value={formData.assistant_id} onValueChange={(value) => handleFormChange('assistant_id', value)}>
                      <SelectTrigger className="bg-neutral-600 text-[14px] w-full placeholder:text-[#71717A] text-[#71717A] !h-[40px] border focus:ring-white border-[#3F3F46]">
                        <SelectValue placeholder="Selecione um assistente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="none">Nenhum assistente</SelectItem>
                          {assistants.filter(a => a.is_active).map((assistant) => (
                            <SelectItem key={assistant.id} value={assistant.id}>
                              {assistant.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Tipo de Gatilho */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="trigger_type">Tipo de Gatilho *</Label>
                    <Select value={formData.trigger_type} onValueChange={(value) => handleFormChange('trigger_type', value)}>
                      <SelectTrigger className="bg-neutral-600 text-[14px] w-full placeholder:text-[#71717A] text-[#71717A] !h-[40px] border focus:ring-white border-[#3F3F46]">
                        <SelectValue placeholder="Selecione o tipo de gatilho" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="first_contact">Primeiro Contato</SelectItem>
                          <SelectItem value="contains">Contém</SelectItem>
                          <SelectItem value="starts_with">Inicia com</SelectItem>
                          <SelectItem value="any">Qualquer mensagem</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Palavras-chave */}
                  {formData.trigger_type !== 'any' && formData.trigger_type !== 'first_contact' && (
                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="keywords">Palavras-chave</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Digite uma palavra-chave"
                          value={keywordInput}
                          onChange={(e) => setKeywordInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                          className="bg-neutral-600 text-[14px] placeholder:text-[#71717A] text-[#71717A] min-h-[36px] border focus:ring-white border-[#3F3F46]"
                          type="text"
                        />
                        <Button type="button" onClick={handleAddKeyword} className="min-w-[80px]">
                          Adicionar
                        </Button>
                      </div>
                      {formData.trigger_keywords.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData.trigger_keywords.map((keyword: string, index: number) => (
                            <div key={index} className="flex items-center gap-2 bg-neutral-500 rounded px-2 py-1">
                              <span className="text-sm text-neutral-50">{keyword}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveKeyword(keyword)}
                                className="text-neutral-300 hover:text-red-400"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pipeline */}
                  <div className="flex flex-col w-full items-start gap-3">
                    <Label htmlFor="pipeline">Pipeline</Label>
                    <Select 
                      value={formData.pipeline_id} 
                      onValueChange={(value) => {
                        handleFormChange('pipeline_id', value);
                        handleFormChange('pipeline_stage_id', ''); // Reset stage when pipeline changes
                      }}
                    >
                      <SelectTrigger className="bg-neutral-600 text-[14px] w-full placeholder:text-[#71717A] text-[#71717A] !h-[40px] border focus:ring-white border-[#3F3F46]">
                        <SelectValue placeholder="Selecione um pipeline" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="none">Nenhum pipeline</SelectItem>
                          {pipelines.map((pipeline) => (
                            <SelectItem key={pipeline.id} value={pipeline.id}>
                              {pipeline.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Etapa do Pipeline */}
                  {formData.pipeline_id && formData.pipeline_id !== 'none' && (
                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="pipeline_stage">Etapa *</Label>
                      <Select value={formData.pipeline_stage_id} onValueChange={(value) => handleFormChange('pipeline_stage_id', value)}>
                        <SelectTrigger className="bg-neutral-600 text-[14px] w-full placeholder:text-[#71717A] text-[#71717A] !h-[40px] border focus:ring-white border-[#3F3F46]">
                          <SelectValue placeholder="Selecione uma etapa" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="none">Nenhuma etapa</SelectItem>
                            {availableStages.map((stage) => (
                              <SelectItem key={stage.id} value={stage.id}>
                                {stage.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="w-full flex justify-end gap-3 mt-4">
                    <Button 
                      type="button"
                      onClick={handleSave}
                      disabled={loading}
                      className="bg-[#E4E4E7] min-w-[120px]"
                    >
                      {loading ? 'Salvando...' : 'Atualizar'}
                    </Button>
                  </div>
                </form>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
        <button 
          onClick={handleDelete}
          disabled={loading}
          className="w-full flex justify-center rounded-[7px] items-center bg-transparent hover:bg-[#27272A] min-h-[29px] text-red-400"
        >
          {loading ? 'Removendo...' : 'Remover'}
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default CampaignEdit;